/**
 * Le nom d'un type de recette, dans la langue de l'écran (ARTO-58). Les cinq
 * services de base se nomment par nos libellés ; un type créé par la
 * direction, par le libellé que l'API sert (`typesRecette` d'une lecture, ou
 * la liste des types) — l'anglais s'il existe, le français sinon. Aucun nom
 * n'est écrit ici : il vient de l'API.
 */
import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { estServiceDeBase } from '@artosera/shared';
import { langueCourante } from '../../lib/modules.js';
import type { LibelleTypeRecette } from '../../types.js';

/** Les libellés des types créés, par identifiant. */
export type LibellesTypes = ReadonlyMap<string, LibelleTypeRecette>;

export const TypesRecetteContexte = createContext<LibellesTypes>(new Map());

/** Les libellés de plusieurs sources, les dernières l'emportant. */
export function libellesTypes(
  ...sources: ReadonlyArray<ReadonlyArray<{ id: string; libelle: LibelleTypeRecette | null }>>
): Map<string, LibelleTypeRecette> {
  const table = new Map<string, LibelleTypeRecette>();
  for (const source of sources) {
    for (const type of source) if (type.libelle !== null) table.set(type.id, type.libelle);
  }
  return table;
}

/** Nomme un type : `court` pour un axe ou une étiquette serrée. */
export function useNomType(): (id: string, court?: boolean) => string {
  const { t, i18n } = useTranslation('console');
  const libelles = useContext(TypesRecetteContexte);
  const anglais = langueCourante(i18n.language) === 'en';
  return (id, court = false) => {
    if (estServiceDeBase(id)) {
      return t(court ? `comptabilite.servicesCourts.${id}` : `comptabilite.services.${id}`);
    }
    const libelle = libelles.get(id);
    if (!libelle) return t('comptabilite.typeInconnu');
    return anglais && libelle.en ? libelle.en : libelle.fr;
  };
}
